import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/Badge";

export interface ModalDeleteProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    badgeText?: string;
    itemName: string;
    itemDescription?: string;
    onConfirm: () => void;
    isDeleting?: boolean;
    submitLabel?: string;
    submittingLabel?: string;
    children?: React.ReactNode;
}

export const ModalDelete: React.FC<ModalDeleteProps> = ({
    isOpen,
    onClose,
    title,
    description,
    badgeText,
    itemName,
    itemDescription,
    onConfirm,
    isDeleting = false,
    submitLabel = "",
    submittingLabel,
    children,
}) => {
    const { t } = useTranslation("global");

    const labelSubmit = submitLabel ? submitLabel : t("modal.delete");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title || t("modal.delete_title")}
            protectChanges={false}
            maxWidth="sm"
            submitVariant="danger"
            onSubmit={onConfirm}
            isSubmitting={isDeleting}
            submitLabel={labelSubmit}
            submittingLabel={submittingLabel ?? t("modal.deleting")}
            cancelLabel={t("modal.cancel")}
        >
            <div className="space-y-4 py-1">
                <p className="text-center text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                    {description || t("modal.delete_desc")}
                </p>

                <div className="text-center p-3.5 bg-slate-50 dark:bg-dark-subtle/60 rounded-xl border border-slate-200/80 dark:border-white/10 space-y-2">
                    {badgeText && (
                        <div>
                            <Badge
                                variant="neutral"
                                size="md"
                                pill
                                className="font-mono font-bold text-xs px-3 py-1 rounded-full bg-white dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-300/80 dark:border-white/15 shadow-2xs"
                            >
                                {badgeText}
                            </Badge>
                        </div>
                    )}

                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                        {itemName}
                    </div>

                    {itemDescription && (
                        <div className="text-xs text-slate-500 dark:text-zinc-400 whitespace-pre-line leading-relaxed max-h-36 overflow-y-auto">
                            {itemDescription}
                        </div>
                    )}
                </div>
                {children}
            </div>
        </Modal>
    );
};
